import { Reveal } from "@/components/Reveal";
import { AdSlot } from "@/components/AdSlot";
import { DTF_PRINT_WIDTH } from "@/config/dtf";
import { formatNum } from "@/lib/dtf";

const steps = [
  {
    title: "Informe largura e altura da estampa",
    text: "Meça a arte em centímetros, do jeito que ela será aplicada na peça.",
  },
  {
    title: "Informe a quantidade",
    text: "Diga quantas unidades daquela mesma estampa você precisa imprimir.",
  },
  {
    title: "A largura útil do filme é considerada",
    text: `O cálculo encaixa as estampas lado a lado dentro dos ${formatNum(DTF_PRINT_WIDTH, 0)} cm de largura do filme DTF têxtil.`,
  },
  {
    title: "O comprimento necessário é calculado",
    text: "A ferramenta soma as faixas utilizadas e converte em centímetros e metros lineares de filme.",
  },
  {
    title: "Você recebe uma estimativa do valor",
    text: "Com o valor do metro linear informado, o custo total e o custo por unidade aparecem na hora.",
  },
];

export const SeoContent = () => (
  <>
    <section
      id="como-funciona"
      className="scroll-mt-20 bg-white py-16 sm:py-20 lg:py-24"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          <Reveal>
            <p className="num text-[11px] uppercase tracking-[0.28em] text-[#f58244]">
              Capítulo 01
            </p>
            <h2
              data-testid="seo-section-what-is-dtf"
              className="mt-3 text-3xl font-extrabold leading-tight text-black sm:text-4xl"
            >
              O que é impressão DTF têxtil?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <article className="space-y-5 text-sm leading-relaxed text-neutral-600 sm:text-base">
              <p>
                DTF é a sigla de <strong className="text-black">Direct to Film</strong>,
                uma técnica em que a arte é impressa em um filme especial de PET
                com tintas próprias para tecido. Depois da impressão, o filme
                recebe uma camada de pó adesivo (hot melt) que é fundido e
                transforma a estampa em um transfer têxtil pronto para ser
                prensado.
              </p>
              <p>
                A aplicação é feita com prensa térmica e funciona em praticamente
                qualquer tecido: algodão, poliéster, tecidos mistos, malha
                fria, dry fit, moletom, sarja, bonés e até bolsas. Por isso a
                impressão DTF têxtil virou o caminho mais prático para
                personalização de camisetas e uniformes sem precisar de matriz ou
                tela.
              </p>
              <p>
                Entre as vantagens estão a alta definição de cores, o toque
                macio, a boa resistência às lavagens, a ausência de custo de
                setup e a liberdade de imprimir uma única peça ou centenas delas
                no mesmo arquivo — aproveitando ao máximo o metro do filme.
              </p>
              <p className="rounded-xl border-l-2 border-[#f58244] bg-[#fff4ed] px-4 py-3 text-[13px] text-neutral-700">
                <strong className="text-black">Importante:</strong> todo o
                conteúdo e os cálculos desta página se referem ao{" "}
                <strong className="text-black">DTF têxtil</strong>, aplicado em
                tecidos com prensa térmica.
              </p>
            </article>
          </Reveal>
        </div>
      </div>
    </section>

    <AdSlot id="in-article" className="pb-4" />

    <section className="bg-[#f6f6f5] py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          <Reveal>
            <p className="num text-[11px] uppercase tracking-[0.28em] text-[#f58244]">
              Capítulo 02
            </p>
            <h2
              data-testid="seo-section-how-to-calculate"
              className="mt-3 text-3xl font-extrabold leading-tight text-black sm:text-4xl"
            >
              Como calcular o custo de impressão DTF têxtil?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <article className="space-y-5 text-sm leading-relaxed text-neutral-600 sm:text-base">
              <p>
                O preço do DTF têxtil por metro linear é a base de tudo: a
                impressão é cobrada pelo comprimento de filme utilizado, e não
                por peça nem por metro quadrado. Um metro linear corresponde a{" "}
                {formatNum(DTF_PRINT_WIDTH, 0)} cm de largura por 100 cm de
                comprimento, então basta saber quantos centímetros de filme suas
                artes consomem.
              </p>
              <p>
                É aí que entra o aproveitamento. O filme tem{" "}
                {formatNum(DTF_PRINT_WIDTH, 0)} cm de largura fixa, então o que
                você paga é o comprimento consumido nessa faixa: duas artes de
                14 x 20 cm encaixadas lado a lado ocupam os mesmos 20 cm
                lineares de uma única arte. Girar a estampa 90° também pode
                reduzir o comprimento — e é exatamente esse encaixe que a
                ferramenta calcula para você.
              </p>
              <p>
                Multiplicando o comprimento necessário pelo valor do metro
                linear você chega ao custo da sua impressão DTF têxtil
                personalizada. Esse é
                o número que vale comparar quando você avalia a terceirização de
                DTF têxtil: além do preço, considere qualidade de tinta, prazo e
                o cuidado na montagem do arquivo.
              </p>
            </article>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="bg-black py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <p className="num text-[11px] uppercase tracking-[0.28em] text-[#f58244]">
            Capítulo 03
          </p>
          <h2 className="mt-3 max-w-2xl text-3xl font-extrabold leading-tight text-white sm:text-4xl">
            Como funciona nossa calculadora DTF têxtil?
          </h2>
        </Reveal>

        <ol className="mt-12 divide-y divide-white/10 border-t border-white/10">
          {steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06}>
              <li
                data-testid={`step-card-${i + 1}`}
                className="group grid gap-3 py-7 sm:grid-cols-[110px_1fr_1.2fr] sm:items-baseline sm:gap-8"
              >
                <span className="num text-2xl font-semibold text-[#f58244] sm:text-3xl">
                  0{i + 1}
                </span>
                <h3 className="text-lg font-bold text-white transition-transform duration-300 group-hover:translate-x-1 sm:text-xl">
                  {s.title}
                </h3>
                <p className="text-sm leading-relaxed text-white/55">{s.text}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  </>
);
